import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { TankDimensions } from 'src/types/ViewModel/Vessel/TankDimensions';
import { TankPosition } from 'src/types/ViewModel/Vessel/TankPosition';
import { TankLayoutCalculation } from 'src/types/ViewModel/Vessel/TankLayoutCalculation';
import { DeckTank, TankColumn, TankRow, TankSectionEnum } from 'src/types/ViewModel/Vessel/TankPlan';

const LAYOUT = {
    HORIZONTAL_GAP_PERCENT: 0.1, // 1% gap between tanks horizontally
    VERTICAL_GAP_PERCENT: 0.1, // 1% gap between tanks vertically
    MINIMUM_TANK_WIDTH: 1, // Minimum width for any tank
    MINIMUM_TANK_WIDTH_PX: 60, // New constant for minimum tank width in pixels
    STANDARD_TANK_WIDTH: 2, // Standard width for tanks when dynamic scaling is disabled
} as const;

const getTotalRows = (tankDimensions: TankDimensions[]): number => {
    // Get the maximum yStart value
    return Math.max(...tankDimensions.map((t) => t.yStart + t.height));
};

const getMaxTotalHeight = (tankDimensions: TankDimensions[]): number => {
    // Find unique start frames (columns)
    const startFrames = Array.from(new Set(tankDimensions.map((t) => t.startFrame)));

    // For each start frame, sum the heights of tanks at that frame position
    const heightsPerPosition = startFrames.map((frame) => {
        const tanksAtPosition = tankDimensions.filter((t) => t.startFrame === frame);
        return tanksAtPosition.reduce((sum, t) => sum + t.height, 0);
    });

    // Return the maximum total height found
    return Math.max(...heightsPerPosition);
};
export const normalizeTankDimensions = (tankDimensions: TankDimensions[], useDynamicScaling: boolean = true): TankDimensions[] => {
    if (tankDimensions.length === 0) return [];

    // Create a deep copy
    const normalizedTanks = tankDimensions.map((t) => ({ ...t }));

    // First normalize all positions to start from 0
    const minStartFrame = Math.min(...normalizedTanks.map((t) => t.startFrame));
    normalizedTanks.forEach((tank) => {
        tank.startFrame -= minStartFrame;
        tank.endFrame -= minStartFrame;
    });

    if (useDynamicScaling) {
        // Calculate current minimum width of any tank
        const currentMinWidth = Math.min(...normalizedTanks.map((t) => t.width));

        // Calculate scale factor needed to ensure minimum width
        const scaleFactor = Math.max(1, LAYOUT.MINIMUM_TANK_WIDTH / currentMinWidth);

        // Apply scaling uniformly to maintain proportions
        normalizedTanks.forEach((tank) => {
            tank.startFrame *= scaleFactor;
            tank.width *= scaleFactor;
            tank.endFrame = tank.startFrame + tank.width;
        });
    } else {
        // Use standard width for all tanks and adjust positions
        let currentPosition = 0;

        // Group tanks by their original start frame to maintain relative positions
        const tanksByStartFrame = normalizedTanks.reduce((groups, tank) => {
            const key = tank.startFrame.toString();
            if (!groups[key]) groups[key] = [];
            groups[key].push(tank);
            return groups;
        }, {} as Record<string, TankDimensions[]>);

        // Sort by original start frame
        const sortedStartFrames = Object.keys(tanksByStartFrame)
            .map(Number)
            .sort((a, b) => a - b);

        // Apply standard width and recalculate positions
        sortedStartFrames.forEach((startFrame) => {
            const tanksAtPosition = tanksByStartFrame[startFrame.toString()];
            tanksAtPosition.forEach((tank) => {
                tank.startFrame = currentPosition;
                tank.width = LAYOUT.STANDARD_TANK_WIDTH;
                tank.endFrame = tank.startFrame + tank.width;
            });
            currentPosition += LAYOUT.STANDARD_TANK_WIDTH;
        });
    }

    return normalizedTanks;
};

export const calculateLayoutDimensions = (tankDimensions: TankDimensions[]): TankLayoutCalculation => {
    const minFrame = 0; // We know this is 0 after normalization
    const maxFrame = Math.max(...tankDimensions.map((t) => t.endFrame));
    const totalWidth = maxFrame; // After normalization, this is just the max endFrame

    const totalVerticalSpace = getTotalRows(tankDimensions);
    const maxTotalHeight = getMaxTotalHeight(tankDimensions);

    const totalVerticalGaps = (Math.ceil(totalVerticalSpace / maxTotalHeight) - 1) * LAYOUT.VERTICAL_GAP_PERCENT;
    const availableHeight = 100 - totalVerticalGaps;
    const unitHeight = availableHeight / totalVerticalSpace;

    return {
        availableWidth: 100,
        availableHeight,
        unitHeight,
        minFrame,
        totalWidth,
    };
};

const normalizePosition = (position: TankPosition): TankPosition => ({
    ...position,
    x: Number(position.x.toFixed(2)),
    y: Number(position.y.toFixed(2)),
    width: Number(position.width.toFixed(2)),
    height: Number(position.height.toFixed(2)),
});

export const calculateTankPosition = (tankDimensions: TankDimensions, layout: TankLayoutCalculation): TankPosition | null => {
    try {
        // Calculate tank dimensions
        const tankWidthPercent = (tankDimensions.width / layout.totalWidth) * layout.availableWidth;
        const tankHeightPercent = tankDimensions.height * layout.unitHeight;

        // Calculate tank position - after normalization, we can use startFrame directly as percentage
        const x = (tankDimensions.startFrame / layout.totalWidth) * layout.availableWidth;
        const y = tankDimensions.yStart * layout.unitHeight;

        return normalizePosition({
            tank: tankDimensions.tank,
            x,
            y,
            width: tankWidthPercent,
            height: tankHeightPercent,
        });
    } catch (error) {
        console.error(`Error calculating position for tank ${tankDimensions.tank.odfjellTankName}:`, error);
        return null;
    }
};

export const getColumnEndFrame = (tanks: Tank[], startFrame: number): number => {
    return tanks.filter((tank) => tank.startFrame === startFrame).reduce((max, current) => Math.max(current.endFrame, max), Number.MIN_VALUE);
};

export const getTanksInColumn = (tanks: Tank[], previousColumnEndFrame: number, startFrame: number, columnEndFrame: number): Tank[] => {
    return tanks.filter(
        (tank) =>
            (tank.startFrame >= startFrame && tank.endFrame <= columnEndFrame) ||
            tank.startFrame === startFrame ||
            tank.endFrame === columnEndFrame ||
            (tank.endFrame > previousColumnEndFrame && tank.endFrame <= columnEndFrame)
    );
};

export const getRows = (tanksInColumn: Tank[]): TankRow[] => {
    if (!tanksInColumn.length) return [];

    const rowYValues = Array.from(new Set(tanksInColumn.map((tank) => tank.yStart)));
    return rowYValues
        .map((y) => ({
            tanks: tanksInColumn.filter((t) => t.yStart === y).sort((a, b) => a.startFrame - b.startFrame),
        }))
        .sort((a, b) => a.tanks[0].yStart - b.tanks[0].yStart);
};

export const getMaxNrOfTanksPerRow = (rows: TankRow[]): number => {
    return rows.reduce((max, current) => Math.max(current.tanks.length, max), 1);
};

export const getMaxNrOfRows = (columns: TankColumn[]): number => {
    return columns.reduce((max, current) => Math.max(current.tankRows.length, max), 0);
};

export const getSortedDeckTanks = (cargoTanks: Tank[]): DeckTank[] => {
    return cargoTanks
        .filter((tank) => tank.section === TankSectionEnum.Deck)
        .sort((a, b) => a.startFrame - b.startFrame)
        .map((tank) => ({ tank, xOffset: 0, width: 0 }));
};

export const calculateMinimumTankPlanWidth = (tankDimensions: TankDimensions[]): number => {
    if (tankDimensions.length === 0) return 0;

    // Get unique frame positions to count number of effective columns
    const uniqueFrames = new Set(tankDimensions.map((t) => t.startFrame));
    const numberOfColumns = uniqueFrames.size;

    // Calculate minimum width based on minimum tank width and number of columns
    return numberOfColumns * LAYOUT.MINIMUM_TANK_WIDTH_PX;
};
